import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-600 text-lg">未找到该漫画</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 古风漫画网免费韩国漫画在线阅读`,
    description: comic.description,
    image: comic.coverImage,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateTime || '',
    dateModified: comic.updateTime,
    publisher: {
      '@type': 'Organization',
      name: '古风漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.gufengmanhua.com/article/${comic.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: comic.category, href: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]}
        />

        <div className="flex flex-col lg:flex-row gap-8 md:gap-10 mb-12">
          <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[280px] mx-auto lg:mx-0">
            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
              <img
                src={comic.coverImage}
                alt={comic.title}
                className="w-full h-full object-cover object-top"
                title={`${comic.title} - 古风漫画网免费韩国漫画在线阅读`}
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
              <span className="text-foreground-300">|</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                comic.status === '连载中'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-secondary-100 text-secondary-700'
              }`}>
                {comic.status}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-600">
                共 <strong>{comic.totalChapters}</strong> 话
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <span className="text-sm font-medium text-accent-500">{comic.rating}</span>
                <span className="text-xs text-foreground-400 ml-1">评分</span>
              </div>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-600">
                <i className="ri-eye-line mr-1"></i>
                {comic.totalViews > 10000
                  ? `${(comic.totalViews / 10000).toFixed(1)}万`
                  : comic.totalViews}{' '}
                次阅读
              </span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-600">
                <i className="ri-time-line mr-1"></i>
                更新于 {comic.updateTime}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
              <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">作品简介</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{comic.fullDescription}</p>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to={`/article/${comic.id}`}
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                开始阅读
              </Link>
              <Link
                to={`/category/${comic.categorySlug}`}
                className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-md font-medium text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回分类
              </Link>
            </div>
          </div>
        </div>

        <section className="mb-12">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
            章节目录
          </h2>

          {comic.chapters.length > 6 && (
            <div className="flex items-center gap-2 mb-4">
              <Link
                to={`/article/${comic.id}`}
                className="px-4 py-1.5 text-xs bg-primary-500 text-background-50 rounded-full cursor-pointer whitespace-nowrap"
              >
                从第1话开始
              </Link>
              <span className="text-xs text-foreground-400">共{comic.totalChapters}话</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {comic.chapters.map((chapter) => (
              <Link
                key={chapter.id}
                to={`/article/${comic.id}`}
                className="flex items-center justify-between p-3 bg-background-100 rounded-md hover:bg-primary-50 transition-colors cursor-pointer group"
              >
                <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {chapter.title}
                </span>
                <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">
                  {chapter.updateTime}
                </span>
              </Link>
            ))}
          </div>

          {comic.totalChapters > 6 && (
            <div className="text-center mt-6">
              <Link
                to={`/article/${comic.id}`}
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
              >
                查看全部 {comic.totalChapters} 话 <i className="ri-arrow-down-line"></i>
              </Link>
            </div>
          )}
        </section>

        {relatedComics.length > 0 && (
          <section>
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}